import { keepPreviousData, useQuery } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { DrawdownFund } from "features/drawdown/DrawdownFund";
import { allocationDetailsSearch } from "gen/endpoints/allocation-rule-lookup/allocation-rule-lookup";
import { AllocationByEntity } from "gen/model/allocationByEntity";
import { AllocationLookupKey } from "gen/model/allocationLookupKey";
import { AllocationLookupSearchKey } from "gen/model/allocationLookupSearchKey";
import { FundEntityReadData } from "gen/model/fundEntityReadData";
import { memoize, omit } from "lodash-es";
import { useMemo } from "react";

export const allocationDetailsSearchQueryKey = (
  fundFamilyId: string,
  payloadKey: AllocationLookupSearchKey[],
  feeChargeEventId: string = "",
  effectiveDate: string = "",
) =>
  [
    `/fund-family/${fundFamilyId}/allocation-rule-lookup/search`,
    payloadKey,
    feeChargeEventId,
    effectiveDate,
  ] as const;

export type GetAllocationsByLookupKeyFn =
  | ((allocationLookupKey: AllocationLookupSearchKey) => DrawdownFund[])
  | undefined;

export const allocationByEntityToDrawdownFund = (
  entity: AllocationByEntity,
): DrawdownFund => ({
  id: entity.entity_id,
  title: entity.entity_name,
  ownershipPercentage: new Decimal(entity.ownership_percentage),
  basisAmount: new Decimal(entity.basis_amount),
  allocationRuleName: entity.allocation_rule_name,
  allocationRuleId: entity.allocation_rule_id,
});

export const useGetAllocationsByLookupKey = (
  fundFamilyId: string,
  lookupKeys: AllocationLookupSearchKey[],
  effectiveDate?: string,
  funds?: FundEntityReadData[],
  feeChargeEventId?: string,
): GetAllocationsByLookupKeyFn => {
  const { data } = useQuery({
    queryKey: allocationDetailsSearchQueryKey(
      fundFamilyId,
      lookupKeys,
      feeChargeEventId,
      effectiveDate,
    ),
    queryFn: async () =>
      allocationDetailsSearch(
        fundFamilyId,
        {
          allocation_lookup_search_keys: lookupKeys,
          event_period_id: feeChargeEventId,
        },
        {
          effective_date: effectiveDate || "",
        },
      ),
    enabled: lookupKeys.length > 0,
    throwOnError: false,
    placeholderData: keepPreviousData,
  });

  const lookupKeyToDrawdownFundDetails = useMemo(() => {
    const result = new Map<string, DrawdownFund[]>();
    data?.forEach(({ allocation_lookup_search_key, allocation_by_entity }) => {
      if (funds) {
        allocation_by_entity = allocation_by_entity.filter((entity) =>
          funds.some((fund) => fund.id === entity.entity_id),
        );
      }
      const drawdownFunds: DrawdownFund[] = allocation_by_entity.map(
        allocationByEntityToDrawdownFund,
      );
      result.set(
        JSON.stringify(omit(allocation_lookup_search_key, ["fund_ids"])),
        drawdownFunds,
      );
    });
    return result;
  }, [data, funds]);

  return useMemo(() => {
    if (!lookupKeyToDrawdownFundDetails.size) {
      return undefined;
    }
    return memoize(
      (allocationLookupKey: AllocationLookupKey) =>
        lookupKeyToDrawdownFundDetails.get(
          JSON.stringify(allocationLookupKey),
        ) || [],
    );
  }, [lookupKeyToDrawdownFundDetails]);
};
